/**
 * "What each platform reports" (VSNP-1957): the posture catalog as a table, per
 * release platform, plus what the gateway and server check on every session and
 * what is not in this release. Shown on the Posture Profiles page, collapsed.
 */

import { CheckCircle2, ShieldCheck } from 'lucide-react';
import {
  ALL_POSTURE_ATTRIBUTES,
  CLIENT_PLATFORMS,
  PLATFORM_CHECKS,
  PLATFORM_LABEL,
  POSTURE_CATALOG,
  platformReport,
  type ClientPlatform,
  type PostureAttribute,
} from '../../data/postureCatalog';

function Cell({ attr, platform }: { attr: PostureAttribute; platform: ClientPlatform }) {
  const r = platformReport(attr, platform);
  if (r === null) return <span className="text-gray-400" title="No Secure Access app for this platform yet">—</span>;
  if (r === 'measured') {
    return (
      <span className="inline-flex items-center gap-1 text-teal-700">
        <CheckCircle2 className="w-3.5 h-3.5" /> measured
      </span>
    );
  }
  return (
    <span className={r.always === 'unknown' ? 'text-amber-700' : 'text-gray-600'}>
      {r.always === 'unknown' ? 'unknown' : `always ${r.always}`}
    </span>
  );
}

const PostureCatalogPanel = () => {
  const deviceFacts = POSTURE_CATALOG.filter((a) => a.origin === 'device');
  const gatewayFacts = POSTURE_CATALOG.filter((a) => a.origin === 'gateway');
  const hidden = ALL_POSTURE_ATTRIBUTES.filter((a) => a.hidden);

  return (
    <details className="card" data-testid="posture-catalog">
      <summary className="cursor-pointer select-none text-sm font-medium text-gray-800">
        What each platform reports
      </summary>
      <div className="mt-3 space-y-4 text-xs text-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left" data-testid="posture-catalog-table">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="py-1.5 pr-4 font-medium">Device fact (self-reported, signed)</th>
                {CLIENT_PLATFORMS.map((p) => (
                  <th key={p} className="py-1.5 pr-4 font-medium" data-platform={p}>
                    {PLATFORM_LABEL[p]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deviceFacts.map((a) => (
                <tr key={a.key} className="border-b border-gray-100 align-top" data-key={a.key}>
                  <td className="py-1.5 pr-4">
                    <div className="font-medium text-gray-900">{a.label}</div>
                    <div className="text-[11px] text-gray-500">
                      <code>{a.key}</code>
                      {a.note ? ` · ${a.note}` : ''}
                    </div>
                  </td>
                  {CLIENT_PLATFORMS.map((p) => (
                    <td key={p} className="py-1.5 pr-4 whitespace-nowrap" data-platform={p}>
                      <Cell attr={a} platform={p} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-[11px] text-gray-500">
            — : no Secure Access app for that platform yet. <span className="text-amber-700">unknown</span> counts as
            not met: a requirement on pass is never met by a device that reports unknown.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="font-medium text-gray-800 mb-1 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-teal-600" /> Checked by the gateway and server
            </p>
            <ul className="space-y-0.5" data-testid="posture-platform-checks">
              {PLATFORM_CHECKS.map((c) => (
                <li key={c.label}>
                  <span className="font-medium">{c.label}</span> — {c.detail}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-gray-500">
              Requirements on the gateway's own facts: {gatewayFacts.map((a) => a.label).join(', ')}.
            </p>
          </div>
          <div>
            <p className="font-medium text-gray-800 mb-1">Not in this release</p>
            <ul className="space-y-0.5" data-testid="posture-not-in-release">
              {hidden.map((a) => (
                <li key={a.key}>
                  <span className="font-medium">{a.label}</span> (<code>{a.key}</code>) — {a.note}
                </li>
              ))}
              <li>
                <span className="font-medium">MDM-vouched and hardware-attested posture</span> — not in this release;
                every device's facts are its own signed report.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </details>
  );
};

export default PostureCatalogPanel;
